import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import React, { useCallback, useState } from 'react';

import DataGridToolbar from '@sophocles/data-grids/components/toolbar';
import DevicePricingConfigGrid from '@sophocles/views/billing/components/DevicePricingConfigGrid';
import FeaturePricingConfigGrid from '@sophocles/views/billing/components/FeaturePricingConfigGrid';

/**
 * UnifiedPricingManager - A unified component for managing both device and feature pricing
 *
 * Supports two modes:
 * 1. Redux mode (default): Pass accountId to load/save configs via Redux/API
 * 2. Controlled mode: Pass value/onChange to manage configs locally (for inline forms like customer creation)
 *
 * @param {string} accountId - The billing account ID (required for Redux mode, omit for controlled mode)
 * @param {string} customerId - The customer ID (used for site group/site autocompletes)
 * @param {string} defaultTab - Default active tab ('device' or 'feature')
 * @param {boolean} isBilledVirtual - Whether virtual billing is enabled
 * @param {string} inventoryPolicy - Inventory policy ('exact', 'none', etc.)
 * @param {Object} value - Controlled mode: { deviceConfigs: [], featureConfigs: [] }
 * @param {function} onChange - Controlled mode: (configs) => void, called when configs change
 * @param {boolean} alwaysEditMode - Start in edit mode and hide edit/save/cancel buttons (for inline forms)
 */
function UnifiedPricingManager({
  accountId,
  customerId,
  defaultTab = 'device',
  isBilledVirtual = false,
  inventoryPolicy = 'exact',
  // Controlled mode props
  value,
  onChange,
  alwaysEditMode = false,
}) {
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Determine if we're in controlled mode
  const isControlled = value !== undefined && onChange !== undefined;

  const handleTabChange = (event, newValue) => {
    if (newValue !== null) {
      setActiveTab(newValue);
    }
  };

  // Handle config changes in controlled mode
  const handleDeviceConfigChange = useCallback(
    configs => {
      if (isControlled && onChange) {
        onChange({
          ...value,
          deviceConfigs: configs,
        });
      }
    },
    [isControlled, onChange, value],
  );

  const handleFeatureConfigChange = useCallback(
    configs => {
      if (isControlled && onChange) {
        onChange({
          ...value,
          featureConfigs: configs,
        });
      }
    },
    [isControlled, onChange, value],
  );

  // Renders the shared DataGridToolbar (variant=flat — pricing lives
  // inside V2 customer settings, so the SettingsSection card already
  // supplies the surface) with the device/feature tab toggle on the
  // left and the active grid's edit/save/cancel buttons on the right.
  const renderToolbar = useCallback(
    buttons => (
      <DataGridToolbar
        variant="flat"
        showQuickFilter={false}
        toolbarContent={
          <ToggleButtonGroup
            value={activeTab}
            exclusive
            onChange={handleTabChange}
            aria-label="pricing type"
            size="small"
          >
            <ToggleButton value="device" aria-label="device pricing">
              Device Pricing
            </ToggleButton>
            <ToggleButton value="feature" aria-label="feature pricing">
              Feature Pricing
            </ToggleButton>
          </ToggleButtonGroup>
        }
        toolbarActions={!alwaysEditMode ? buttons : null}
      />
    ),
    [activeTab, alwaysEditMode],
  );

  return (
    <Box>
      {activeTab === 'device' && (
        <DevicePricingConfigGrid
          accountId={accountId}
          customerId={customerId}
          renderToolbar={renderToolbar}
          isBilledVirtual={isBilledVirtual}
          inventoryPolicy={inventoryPolicy}
          // Controlled mode props
          value={isControlled ? value?.deviceConfigs : undefined}
          onChange={isControlled ? handleDeviceConfigChange : undefined}
          alwaysEditMode={alwaysEditMode}
        />
      )}

      {activeTab === 'feature' && (
        <FeaturePricingConfigGrid
          accountId={accountId}
          customerId={customerId}
          renderToolbar={renderToolbar}
          // Controlled mode props
          value={isControlled ? value?.featureConfigs : undefined}
          onChange={isControlled ? handleFeatureConfigChange : undefined}
          alwaysEditMode={alwaysEditMode}
        />
      )}
    </Box>
  );
}

export default UnifiedPricingManager;
